import { z } from '../zod.js';

export const WEBHOOK_EVENT = Object.freeze({
  WEBHOOK_TEST: 'webhook.test',
  CUSTOMER_CREATED: 'customer.created',
  CUSTOMER_UPDATED: 'customer.updated',
  PAYMENT_ABANDONED: 'payment.abandoned',
  PAYMENT_CREATED: 'payment.created',
  PAYMENT_PAID: 'payment.paid',
  PAYMENT_CANCELED: 'payment.canceled',
  BOOKING_ABANDONED: 'booking.abandoned',
  BOOKING_CREATED: 'booking.created',
  BOOKING_PAID: 'booking.paid',
  BOOKING_CANCELED: 'booking.canceled',
  TICKET_CREATED: 'ticket.created',
  TICKET_CANCELED: 'ticket.canceled',
  SUBMISSION_CREATED: 'submission.created',
  SUBMISSION_UPDATED: 'submission.updated',
  SUBMISSION_CANCELED: 'submission.canceled',
  CHECKOUT_PAGE_CREATED: 'checkout_page.created',
  CHECKOUT_PAGE_UPDATED: 'checkout_page.updated',
  PRODUCT_CREATED: 'product.created',
  PRODUCT_UPDATED: 'product.updated',
  SUBSCRIPTION_ABANDONED: 'subscription.abandoned',
  SUBSCRIPTION_CREATED: 'subscription.created',
  SUBSCRIPTION_TRIALING: 'subscription.trialing',
  SUBSCRIPTION_SCHEDULED: 'subscription.scheduled',
  SUBSCRIPTION_ACTIVE: 'subscription.active',
  SUBSCRIPTION_COMPLETED: 'subscription.completed',
  SUBSCRIPTION_CANCELED: 'subscription.canceled',
  SUBSCRIPTION_EXPIRED: 'subscription.expired',
} as const);
export type WEBHOOK_EVENT = (typeof WEBHOOK_EVENT)[keyof typeof WEBHOOK_EVENT];

export const WebhookEventValidationEnum = z.enum(WEBHOOK_EVENT).meta({
  description: `An event a webhook can subscribe to. Valid values: ${Object.values(WEBHOOK_EVENT).join(', ')}. \`webhook.test\` is the payload sent by the dashboard's test-delivery action.`,
});

// `webhook.test` is only ever emitted by the dashboard's test-delivery action — it has no
// registered event meta and is never delivered, so subscribing to it would burn a webhook slot.
const { WEBHOOK_TEST: _webhookTest, ...SUBSCRIBABLE_EVENTS } = WEBHOOK_EVENT;
export const WEBHOOK_SUBSCRIBABLE_EVENT = Object.freeze(SUBSCRIBABLE_EVENTS);
export type WEBHOOK_SUBSCRIBABLE_EVENT =
  (typeof WEBHOOK_SUBSCRIBABLE_EVENT)[keyof typeof WEBHOOK_SUBSCRIBABLE_EVENT];

export const WebhookSubscribableEventValidationEnum = z.enum(WEBHOOK_SUBSCRIBABLE_EVENT).meta({
  description: `An event a webhook can subscribe to. Valid values: ${Object.values(WEBHOOK_SUBSCRIBABLE_EVENT).join(', ')}.`,
});

export const WEBHOOK_STATUS = Object.freeze({
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  FAILED: 'failed',
} as const);
export type WEBHOOK_STATUS = (typeof WEBHOOK_STATUS)[keyof typeof WEBHOOK_STATUS];

export const WebhookStatusValidationEnum = z.enum(WEBHOOK_STATUS).meta({
  description:
    '`active` receives deliveries, `inactive` is paused by the seller, `failed` is set by Checkout Page after repeated delivery failures.',
});

export const WEBHOOK_UPDATABLE_STATUS = [WEBHOOK_STATUS.ACTIVE, WEBHOOK_STATUS.INACTIVE] as const;

export const WebhookUpdatableStatusValidationEnum = z.enum(WEBHOOK_UPDATABLE_STATUS).meta({
  description:
    'Set `inactive` to pause deliveries without deleting the endpoint, `active` to resume. `failed` is system-managed and cannot be set.',
});

export const WEBHOOK_VERSION = Object.freeze({
  V1: 'v1',
} as const);
export type WEBHOOK_VERSION = (typeof WEBHOOK_VERSION)[keyof typeof WEBHOOK_VERSION];

export const WebhookVersionValidationEnum = z.enum(WEBHOOK_VERSION).meta({
  description: 'Payload schema version delivered to the endpoint.',
});
